import { useCallback, useMemo, useRef } from "react";
import { useAppDispatch, useAppStore } from "@/store/app/hooks";
import { catalogFingerprint } from "@/features/sparxsEditor/lib/catalog/catalogFingerprint";
import { getDeepLinkedSparxsEditorFileName } from "@/features/sparxsEditor/lib/catalog/deepLinkedSparxsEditorFileName";
import { runSearchIndexPass } from "@/features/sparxsEditor/lib/advancedSearch/index/pass/runSearchIndexPass";
import { useActiveFileName } from "@/features/sparxsEditor/hooks/activeData/useActiveFileName";

export type UseSparxsSearchIndexResult = {
  buildIndex: (expectedFingerprint: string) => void;
  cancelBuildIndex: () => void;
};

export const useSearchIndexLoad = (): UseSparxsSearchIndexResult => {
  const dispatch = useAppDispatch();
  const store = useAppStore();
  const activeFileName = useActiveFileName();

  const activeRef = useRef(activeFileName);
  activeRef.current = activeFileName;

  const indexRunIdRef = useRef(0);
  const teardownRef = useRef<(() => void) | null>(null);

  const cancelBuildIndex = useCallback(() => {
    teardownRef.current?.();
    teardownRef.current = null;
  }, []);

  const buildIndex = useCallback(
    (expectedFingerprint: string) => {
      cancelBuildIndex();
      const { data, excludedData } = store.getState().editor.sparxsList;
      const computed = catalogFingerprint(data, excludedData);
      if (computed !== expectedFingerprint) {
        return;
      }
      const teardown = runSearchIndexPass({
        store,
        dispatch,
        getActiveFileName: () => {
          const fromPath = getDeepLinkedSparxsEditorFileName(
            window.location.pathname,
          );
          if (fromPath != null && fromPath.length > 0) {
            return fromPath;
          }
          return activeRef.current;
        },
        indexRunIdRef,
      });
      if (teardown != null) {
        teardownRef.current = teardown;
      }
    },
    [cancelBuildIndex, dispatch, store],
  );

  return useMemo(
    () => ({
      buildIndex,
      cancelBuildIndex,
    }),
    [buildIndex, cancelBuildIndex],
  );
};
